if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData = new FormData(this.form);
        const personalisationWidget = this.form.querySelector('[data-wwfc-personalisation]');
        const personalisationType = formData.get('properties[Personalisation]');
        const feeVariantId = personalisationWidget?.dataset.feeVariantId;
        const shouldAddPersonalisationFee = personalisationType && feeVariantId;
        const sectionsToRender = this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : null;
        const config = shouldAddPersonalisationFee ? fetchConfig('javascript') : fetchConfig('javascript');

        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        if (this.cart) {
          this.cart.setActiveElement(document.activeElement);
        }

        if (shouldAddPersonalisationFee) {
          const properties = {};

          formData.forEach((value, key) => {
            const propertyMatch = key.match(/^properties\[(.+)\]$/);
            if (propertyMatch && value) {
              properties[propertyMatch[1]] = value;
            }
          });

          const quantity = Number(formData.get('quantity') || 1);
          const personalisationRef = `WWFC-${Date.now().toString(36).toUpperCase()}-${Math.random()
            .toString(36)
            .slice(2, 6)
            .toUpperCase()}`;

          properties['Personalisation Ref'] = personalisationRef;

          const feeProperties = {
            ...properties,
            'Personalisation Ref': personalisationRef,
            'Personalisation fee for': personalisationWidget.dataset.productTitle,
          };

          const payload = {
            items: [
              {
                id: Number(formData.get('id')),
                quantity,
                properties,
              },
              {
                id: Number(feeVariantId),
                quantity,
                properties: feeProperties,
              },
            ],
          };

          if (sectionsToRender) {
            payload.sections = sectionsToRender;
            payload.sections_url = window.location.pathname;
          }

          config.body = JSON.stringify(payload);
        } else {
          delete config.headers['Content-Type'];

          if (sectionsToRender) {
            formData.append('sections', sectionsToRender);
            formData.append('sections_url', window.location.pathname);
          }

          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              await this.syncWwfcPersonalisationCartNote();
              window.location = window.routes.cart_url;
              return;
            }

            await this.syncWwfcPersonalisationCartNote();

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      async syncWwfcPersonalisationCartNote() {
        try {
          const cartResponse = await fetch(`${routes.cart_url}.js`, {
            headers: { Accept: 'application/json' },
          });
          if (!cartResponse.ok) return;

          const cart = await cartResponse.json();
          const lines = [];

          cart.items.forEach((item, index) => {
            const options = item.options_with_values || [];
            const personalisation = options.find((option) =>
              option.name.toLowerCase().includes('personalisation') ||
              option.name.toLowerCase().includes('personalization')
            );

            if (!personalisation || personalisation.value === 'No personalisation') return;

            const size = options.find((option) => option.name.toLowerCase().includes('size'));
            const properties = item.properties || {};
            const details =
              properties['Personalisation Details'] ||
              [
                properties['Custom Name'] ? `Name: ${properties['Custom Name']}` : '',
                properties['Custom Number'] ? `Number: ${properties['Custom Number']}` : '',
              ]
                .filter(Boolean)
                .join(' / ');

            lines.push(
              [
                `${index + 1}. ${item.product_title}`,
                size ? `Size: ${size.value}` : '',
                `Personalisation: ${personalisation.value}`,
                details,
              ]
                .filter(Boolean)
                .join(' | ')
            );
          });

          const marker = 'WWFC PERSONALISATION DETAILS';
          const existingNote = cart.note || '';
          const baseNote = existingNote.includes(marker) ? existingNote.split(marker)[0].trim() : existingNote.trim();
          const personalisationNote = lines.length ? `${marker}\n${lines.join('\n')}` : '';
          const nextNote = [baseNote, personalisationNote].filter(Boolean).join('\n\n');

          await fetch(`${routes.cart_update_url}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              note: nextNote,
              attributes: {
                'WWFC Personalisation': personalisationNote,
              },
            }),
          });
        } catch (error) {
          console.warn('Could not sync WWFC personalisation cart note', error);
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
